import { Box } from '@mui/material';
import { BreakpointProperties } from '../../../../hooks/useBreakpoint';
import { cfFreezeNames } from '../../../../theme/theme';
import {
  CfDataGridColumnDefinition,
  CfDataGridDisplayMode,
  CfDataGridForStoreDataProps,
  CfDataGridProps,
  CfDataGridTableViewDefinition,
  CfRecomputeRowHeightsRefCallback,
  NavigableElement,
} from '../../CfDataGridProps';
import { getPropertyByBreakpoint } from '../../utility/breakpoint';
import { getIsLoading } from '../../utility/data-grid-props';
import { KeyboardNavigator } from '../../utility/keyboard-navigation';
import {
  StyledGridHeaderCell,
  StyledGridHeaderContainer,
  StyledGridHeaderRow,
  StyledStickyTableHead,
  StyledTableContainer,
} from './CfDataGridTableView.styles';
import { CfDataGridTableViewBody } from './body/CfDataGridTableViewBody';
import { CfDataGridTableViewDragDropBody } from './body/CfDataGridTableViewDragDropBody';
import { CfDataGridTableViewVirtualizedBody } from './body/CfDataGridTableViewVirtualizedBody';

export function CfDataGridTableView<TItem, TAppState>(props: {
  dataGridProps: CfDataGridProps<TItem> | CfDataGridForStoreDataProps<TItem, TAppState>;
  keyboardNavigator: KeyboardNavigator;
  breakpointProperties: BreakpointProperties;
  onNextPageRequested: (pageIndex: number) => void;
  recomputeRowHeightsRef: CfRecomputeRowHeightsRefCallback;
}): JSX.Element {
  if (!props.dataGridProps.tableView) return <></>;

  const breakpoint = props.breakpointProperties.breakpoint;
  const tableViewDefinition = getPropertyByBreakpoint<CfDataGridTableViewDefinition<TItem>>(
    props.dataGridProps.tableView,
    breakpoint
  );

  props.keyboardNavigator.reset();
  const setNavigableElement = (navigableElement: NavigableElement, rowIndex: number, columnIndex: number) =>
    props.keyboardNavigator.registerNavigableElement(navigableElement, { rowIndex, columnIndex });
  const focusClosestEnabledElement = (rowIndex: number, elementIndex?: number) =>
    props.keyboardNavigator.focusClosestEnabledElement(rowIndex, elementIndex);

  const isLoading = getIsLoading(props.dataGridProps);

  const tableHeadContent =
    tableViewDefinition.customHeader && !isLoading ? (
      tableViewDefinition.customHeader
    ) : (
      <CfDataGridTableViewHeader tableViewDefinition={tableViewDefinition} />
    );

  const tableHead = tableViewDefinition.isTableHeadSticky ? (
    <StyledStickyTableHead
      data-testid={props.dataGridProps.headingsTestId}
      className={cfFreezeNames.tableHeader}
      tableClassName={props.dataGridProps.headerClassName}
      sx={tableViewDefinition.tableHeadContainerSx}
    >
      {tableHeadContent}
    </StyledStickyTableHead>
  ) : (
    <Box
      data-testid={props.dataGridProps.headingsTestId}
      className={cfFreezeNames.tableHeader}
      sx={tableViewDefinition.tableHeadContainerSx}
    >
      {tableHeadContent}
    </Box>
  );

  return (
    <StyledTableContainer>
      <Box {...tableViewDefinition.tableProps}>
        {tableHead}

        {tableViewDefinition.dragDropProps && !isLoading ? (
          <CfDataGridTableViewDragDropBody
            dataGridProps={props.dataGridProps}
            tableViewDefinition={tableViewDefinition}
            setNavigableElement={setNavigableElement}
            breakpointProperties={props.breakpointProperties}
            focusClosestEnabledElement={focusClosestEnabledElement}
          />
        ) : tableViewDefinition.virtualizedProps ? (
          <CfDataGridTableViewVirtualizedBody
            dataGridProps={props.dataGridProps}
            tableViewDefinition={tableViewDefinition}
            setNavigableElement={setNavigableElement}
            breakpointProperties={props.breakpointProperties}
            onNextPageRequested={props.onNextPageRequested}
            focusClosestEnabledElement={focusClosestEnabledElement}
            recomputeRowHeightsRef={props.recomputeRowHeightsRef}
          />
        ) : (
          <CfDataGridTableViewBody
            dataGridProps={props.dataGridProps}
            tableViewDefinition={tableViewDefinition}
            setNavigableElement={setNavigableElement}
          />
        )}
      </Box>
    </StyledTableContainer>
  );
}

export const RoundedTableHeadStyles: Partial<CfDataGridTableViewDefinition<unknown>> = {
  tableHeadSx: {
    border: (theme) => `${theme.spacing(2)} solid ${theme.palette.common.white}`,
    borderBottom: 'none',
    mx: -2,
    mt: -2,
    borderRadius: '8px 8px 0 0',
    background: 'none',
    ' > *': {
      background: (theme) => theme.palette.grey[200],
      borderRadius: '4px 4px 0 0',
    },
  },
  tableHeadContainerSx: {
    border: (theme) => `${theme.spacing(2)} solid ${theme.palette.background.default}`,
    borderBottom: 'none',
    mx: -2,
    mt: -2,
    borderRadius: '0',
  },
};

function CfDataGridTableViewHeader<TItem>(props: {
  tableViewDefinition: CfDataGridTableViewDefinition<TItem>;
}): JSX.Element {
  return (
    <StyledGridHeaderContainer
      sx={{
        visibility: props.tableViewDefinition.showHeader ? 'visible' : 'hidden',
        ...props.tableViewDefinition.tableHeadSx,
      }}
    >
      <StyledGridHeaderRow
        sx={{
          ...(props.tableViewDefinition.showHeader && { minHeight: 32 }),
        }}
      >
        {props.tableViewDefinition.columns.map((col: CfDataGridColumnDefinition<TItem>, index: number) => (
          <StyledGridHeaderCell
            key={index}
            {...col.headerCellProps}
            sx={{
              width: col.width,
              textAlign: col.horizontalAlignment,
              borderTopRightRadius: (theme) => theme.shape.borderRadius,
              ...(props.tableViewDefinition.showHeader ? {} : { paddingY: 0, maxHeight: 0 }),
              ...col.headerCellProps?.sx,
            }}
          >
            {props.tableViewDefinition.showHeader && col.title}
          </StyledGridHeaderCell>
        ))}
      </StyledGridHeaderRow>
    </StyledGridHeaderContainer>
  );
}
